import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import { menuItems } from "./Sidebar";
import "../assets/styles/adminTour.css";

const normalisePath = (value = "") => {
  const cleanPath = String(value).split("?")[0].replace(/\/+$/, "");
  return cleanPath || "/";
};

const PAGE_CAPABILITY_COPY = {
  Dashboard: {
    view: "See your profile, account status, and recent sign-in information.",
    edit: "Update your profile and account details.",
  },
  "Admin Dashboard": {
    view: "Monitor sales, orders, customers, and overall store activity.",
  },
  Users: {
    view: "See the administrators who can use this panel.",
    add: "Add new administrators and assign their responsibilities.",
    edit: "Update administrator details, status, and assigned role.",
    delete: "Remove administrator accounts that are no longer needed.",
  },
  Customers: {
    view: "Look up customer accounts and their information.",
    add: "Create a customer account when assistance is needed.",
    edit: "Update customer details or block and reactivate accounts.",
    delete: "Remove customer records when permitted by your process.",
  },
  Categories: {
    view: "Browse the categories used to organise the catalogue.",
    add: "Add a new product category.",
    edit: "Rename categories or change their details.",
    delete: "Remove categories that are no longer used.",
  },
  "Option Templates": {
    view: "See reusable product choices such as paper, size, or finish.",
    add: "Build new option templates for products.",
    edit: "Change template fields, choices, and pricing rules.",
    delete: "Remove option templates that are no longer required.",
  },
  Products: {
    view: "Browse products and check their details and availability.",
    add: "Add individual products or upload products in bulk.",
    edit: "Change product information, pricing, images, and options.",
    delete: "Remove products from the catalogue.",
  },
  "HSN & GST": {
    view: "See the tax classes and HSN codes used for billing.",
    add: "Add tax classes for new product types.",
    edit: "Update HSN codes and GST rates.",
    delete: "Remove tax classes that are no longer used.",
  },
  Collections: {
    view: "See how products are grouped into collections.",
    add: "Create new collections for the storefront.",
    edit: "Change collection details and included products.",
    delete: "Remove collections that are no longer displayed.",
  },
  Forms: {
    view: "See the forms used to collect customer information.",
    add: "Build new customer forms.",
    edit: "Change form questions, fields, and settings.",
    delete: "Remove forms that are no longer needed.",
  },
  Orders: {
    view: "Review orders, customer requirements, and payment information.",
    add: "Create an order on behalf of a customer.",
    edit: "Update order details, progress, and status.",
    delete: "Remove orders when your business process allows it.",
  },
  Production: {
    view: "See which orders are waiting, in production, or completed.",
    add: "Add work to the production workflow.",
    edit: "Move jobs through production stages and update their progress.",
    delete: "Remove a job from the production workflow.",
  },
  Vendors: {
    view: "View printing partners and the production orders assigned to them.",
    add: "Add a printing vendor without creating a login account.",
    edit: "Update vendor contacts, capabilities, and active status.",
    delete: "Remove vendors that have no associated production orders.",
  },
  Invoices: {
    view: "Open invoices and review billing and tax details.",
    add: "Generate invoices for eligible orders.",
    edit: "Correct invoice details where changes are allowed.",
    delete: "Remove invoices when your accounting process allows it.",
  },
  Disputes: {
    view: "Review customer concerns and their conversation history.",
    add: "Open a new dispute record when needed.",
    edit: "Reply to customers, add internal notes, and resolve disputes.",
    delete: "Remove dispute records when appropriate.",
  },
  Notifications: {
    view: "Read alerts about activity that may need your attention.",
    add: "Create notifications for other administrators.",
    edit: "Mark notifications as read and manage their status.",
    delete: "Clear notifications that are no longer needed.",
  },
  "Notification Control": {
    view: "See how administrative notifications are routed.",
    add: "Create notification routing rules.",
    edit: "Choose which teams receive each type of alert.",
    delete: "Remove notification rules that are no longer needed.",
  },
  "Roles & Permissions": {
    view: "See admin roles and what each role is allowed to manage.",
    add: "Create roles for different team responsibilities.",
    edit: "Change a role and the pages its members can use.",
    delete: "Remove roles that are no longer assigned.",
  },
  Settings: {
    view: "Review the settings used across the store.",
    add: "Add new configurable settings where available.",
    edit: "Change store, tax, invoice, and other platform settings.",
    delete: "Remove optional settings that are no longer used.",
  },
  Maintenance: {
    view: "Check whether the customer website is open or in maintenance mode.",
    edit: "Turn maintenance mode on or off and update its message.",
  },
  "Promotional Popups": {
    view: "See the promotional messages shown to customers.",
    add: "Create a new promotional popup.",
    edit: "Change popup content, timing, and visibility.",
    delete: "Remove promotions that have ended.",
  },
  "Email Newsletters": {
    view: "Review email campaigns, templates, and subscribed customers.",
    add: "Create newsletter templates and new campaigns.",
    edit: "Update, test, schedule, send, or cancel campaigns.",
    delete: "Remove newsletter templates that are no longer used.",
  },
};

const getCapabilities = (page, item) => {
  const copy = PAGE_CAPABILITY_COPY[item.label] || {};
  const pageName = item.label.toLowerCase();
  const capabilities = [copy.view || `Open and review the ${pageName} page.`];

  if (page.can_add) {
    capabilities.push(copy.add || `Add new information on the ${pageName} page.`);
  }

  if (page.can_edit) {
    capabilities.push(copy.edit || `Update information on the ${pageName} page.`);
  }

  if (page.can_delete) {
    capabilities.push(copy.delete || `Remove information from the ${pageName} page.`);
  }

  return capabilities;
};

const findPermission = (pages, item) =>
  pages.find(
    (page) =>
      normalisePath(page.path) === normalisePath(item.path) ||
      String(page.name || "").trim().toLowerCase() ===
        String(item.permissionName || item.label || "")
          .trim()
          .toLowerCase()
  );

export const buildTourSteps = ({ pages = [], role = {}, userName = "Admin" }) => {
  const accessiblePages = pages.filter((page) => page.can_view !== false);
  const roleName = role.role_name || "Administrator";
  const groupSteps = menuItems
    .map((group) => ({
      group,
      pages: group.items
        .map((item) => {
          const permission = findPermission(accessiblePages, item);
          return permission
            ? {
                label: item.label,
                capabilities: getCapabilities(permission, item),
              }
            : null;
        })
        .filter(Boolean),
    }))
    .filter(({ pages: permittedPages }) => permittedPages.length)
    .map(({ group, pages: permittedPages }) => ({
      selector: `[data-tour-group="${group.label}"]`,
      title: `${group.label} section`,
      description:
        "Here is what your account can do in this section. Expand it from the navigation whenever you need these pages.",
      pages: permittedPages,
    }));

  const canViewNotifications = accessiblePages.some(
    (page) => String(page.name || "").trim().toLowerCase() === "notifications"
  );

  return [
    {
      title: `Welcome, ${userName}`,
      eyebrow: "Your admin panel tour",
      description: role.is_super_admin
        ? "You have full platform access. Let’s take a quick look at the workspace and where the main tools live."
        : `This walkthrough is tailored to your ${roleName} permissions, so it only introduces tools your role can use.`,
    },
    {
      selector: "[data-tour=\"workspace-title\"]",
      title: "Your workspace",
      description:
        "The navigation stays on the left and the current page opens in this main workspace. On a smaller screen, use the menu button to open the navigation.",
    },
    ...groupSteps,
    ...(canViewNotifications
      ? [
          {
            selector: "[data-tour=\"notifications\"]",
            title: "Stay up to date",
            description:
              "Open notifications here to see new activity that needs your attention.",
          },
        ]
      : []),
    {
      selector: "[data-tour=\"admin-profile\"]",
      title: "Your admin account",
      description:
        "This area shows which admin is signed in. Your profile dashboard contains your account and security details.",
    },
    {
      selector: "[data-tour=\"restart-guide\"]",
      title: "Replay this guide anytime",
      description:
        "Select Guide in the top navigation whenever you want to run this walkthrough again.",
    },
  ];
};

const getTooltipPosition = (rect) => {
  if (!rect) {
    return {
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
    };
  }

  const gap = 18;
  const tooltipWidth = Math.min(390, window.innerWidth - 32);

  if (window.innerWidth <= 720) {
    return {
      left: 12,
      right: 12,
      bottom: 12,
    };
  }

  if (window.innerWidth - rect.right >= tooltipWidth + gap) {
    return {
      left: rect.right + gap,
      top: "50%",
      transform: "translateY(-50%)",
    };
  }

  if (rect.left >= tooltipWidth + gap) {
    return {
      left: rect.left - tooltipWidth - gap,
      top: "50%",
      transform: "translateY(-50%)",
    };
  }

  return {
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
  };
};

const AdminTour = ({ restartToken = 0, onActiveChange }) => {
  const { user } = useAuth();
  const [onboarding, setOnboarding] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const restartRequestedRef = useRef(false);
  const tooltipRef = useRef(null);

  const steps = useMemo(
    () =>
      onboarding
        ? buildTourSteps({
            pages: onboarding.pages,
            role: onboarding.role,
            userName: user?.name || "Admin",
          })
        : [],
    [onboarding, user?.name]
  );

  const beginTour = useCallback(() => {
    setStepIndex(0);
    setIsOpen(true);
    onActiveChange?.(true);
  }, [onActiveChange]);

  useEffect(() => {
    let active = true;

    const loadOnboarding = async () => {
      try {
        const response = await api.get("/admin/onboarding");
        const data = response.data?.data;

        if (!active || !data) return;

        setOnboarding(data);

        if (!data.completed || restartRequestedRef.current) {
          restartRequestedRef.current = false;
          setStepIndex(0);
          setIsOpen(true);
          onActiveChange?.(true);
        }
      } catch (error) {
        console.error("Admin tutorial could not be loaded:", error);
      }
    };

    loadOnboarding();

    return () => {
      active = false;
    };
  }, [onActiveChange]);

  useEffect(() => {
    if (!restartToken) return;

    if (onboarding) {
      beginTour();
    } else {
      restartRequestedRef.current = true;
    }
  }, [restartToken, onboarding, beginTour]);

  const currentStep = steps[stepIndex];

  const updateTarget = useCallback(() => {
    if (!isOpen || !currentStep?.selector) {
      setTargetRect(null);
      return;
    }

    const target = document.querySelector(currentStep.selector);

    if (!target) {
      setTargetRect(null);
      return;
    }

    const rect = target.getBoundingClientRect();
    const padding = 7;

    setTargetRect({
      top: Math.max(8, rect.top - padding),
      left: Math.max(8, rect.left - padding),
      right: Math.min(window.innerWidth - 8, rect.right + padding),
      bottom: Math.min(window.innerHeight - 8, rect.bottom + padding),
      width: Math.min(window.innerWidth - 16, rect.width + padding * 2),
      height: Math.min(window.innerHeight - 16, rect.height + padding * 2),
    });
  }, [currentStep, isOpen]);

  useEffect(() => {
    if (!isOpen || !currentStep) return undefined;

    const target = currentStep.selector
      ? document.querySelector(currentStep.selector)
      : null;

    target?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    const measureTimer = window.setTimeout(updateTarget, 220);

    window.addEventListener("resize", updateTarget);
    window.addEventListener("scroll", updateTarget, true);

    return () => {
      window.clearTimeout(measureTimer);
      window.removeEventListener("resize", updateTarget);
      window.removeEventListener("scroll", updateTarget, true);
    };
  }, [currentStep, isOpen, updateTarget]);

  useEffect(() => {
    if (!isOpen) return undefined;

    document.body.classList.add("admin-tour-open");
    tooltipRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === "ArrowRight" && stepIndex < steps.length - 1) {
        setStepIndex((value) => value + 1);
      }

      if (event.key === "ArrowLeft" && stepIndex > 0) {
        setStepIndex((value) => value - 1);
      }

      if (event.key === "Escape") {
        setIsOpen(false);
        onActiveChange?.(false);
        api.post("/admin/onboarding/complete").catch(() => undefined);
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.classList.remove("admin-tour-open");
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onActiveChange, stepIndex, steps.length]);

  const closeTour = () => {
    setIsOpen(false);
    onActiveChange?.(false);

    api.post("/admin/onboarding/complete").catch((error) => {
      console.error("Admin tutorial progress could not be saved:", error);
    });
  };

  if (!isOpen || !currentStep) return null;

  const isLastStep = stepIndex === steps.length - 1;
  const tooltipStyle = getTooltipPosition(targetRect);

  return (
    <div className="admin-tour-layer" aria-live="polite">
      <div className="admin-tour-blocker" />

      {targetRect ? (
        <div
          className="admin-tour-spotlight"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
        />
      ) : (
        <div className="admin-tour-full-shade" />
      )}

      <section
        ref={tooltipRef}
        className="admin-tour-card"
        style={tooltipStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-tour-title"
        aria-describedby="admin-tour-description"
        tabIndex={-1}
      >
        <div className="admin-tour-card-header">
          <div>
            <span>{currentStep.eyebrow || "Admin panel guide"}</span>
            <h2 id="admin-tour-title">{currentStep.title}</h2>
          </div>

          <button
            type="button"
            className="admin-tour-close"
            aria-label="Close admin panel guide"
            onClick={closeTour}
          >
            <i className="pi pi-times" />
          </button>
        </div>

        <p id="admin-tour-description">{currentStep.description}</p>

        {currentStep.pages?.length ? (
          <div className="admin-tour-permission-list">
            {currentStep.pages.map((page) => (
              <div className="admin-tour-permission-row" key={page.label}>
                <strong>{page.label}</strong>
                <ul>
                  {page.capabilities.map((capability) => (
                    <li key={capability}>
                      <i className="pi pi-check-circle" />
                      <span>{capability}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : null}

        <div className="admin-tour-progress" aria-hidden="true">
          {steps.map((step, index) => (
            <span
              className={index === stepIndex ? "active" : ""}
              key={`${step.title}-${index}`}
            />
          ))}
        </div>

        <div className="admin-tour-footer">
          <span>
            {stepIndex + 1} of {steps.length}
          </span>

          <div className="admin-tour-actions">
            {stepIndex > 0 ? (
              <button
                type="button"
                className="admin-tour-back"
                onClick={() => setStepIndex((value) => value - 1)}
              >
                Back
              </button>
            ) : (
              <button
                type="button"
                className="admin-tour-back"
                onClick={closeTour}
              >
                Skip
              </button>
            )}

            <button
              type="button"
              className="admin-tour-next"
              onClick={() => {
                if (isLastStep) {
                  closeTour();
                  return;
                }

                setStepIndex((value) => value + 1);
              }}
            >
              {isLastStep ? "Finish" : "Next"}
              <i className={`pi ${isLastStep ? "pi-check" : "pi-arrow-right"}`} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminTour;
